import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from 'src/components/ui/sheet';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from 'src/components/ui/avatar';
import { Card, CardContent } from 'src/components/ui/card';
import { Upload, ArrowRight, Info, Trash2, Terminal } from 'lucide-react';
import { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import useAuth from 'src/hooks/useAuth';
import { upload, uploadTransformation } from 'src/hooks/useUpload';
import { useSelector } from 'react-redux';
import {
  setDoc,
  collection,
  serverTimestamp,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { selectCurrentWorkspaceId } from 'src/redux/slices/userSlice';
import { DB, StoragePublicAssets } from 'src/contexts/FirebaseContext';
import useTransformations, { Transformation } from 'src/hooks/useTransformations';
import { TransformationImageCard } from '@simplylabs_/shared';
import { Trans, useTranslation } from 'react-i18next';
import { Badge } from 'src/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from 'src/components/ui/alert';
import { REVIEW } from 'src/theme/palette';

const getExtensionFromMimeType = (
  mimeType: string | undefined,
  fileName: string
): string | undefined => {
  if (!mimeType) return fileName.split('.').pop();
  const mimeMap: { [key: string]: string } = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
  };
  return mimeMap[mimeType] || fileName.split('.').pop();
};

export default function TransformationPictures() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const workspaceId = useSelector(selectCurrentWorkspaceId);
  const [isUploading, setIsUploading] = useState(false);
  const { transformations, loading } = useTransformations();
  const [selectedTransformation, setSelectedTransformation] = useState<Transformation | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    if (selectedTransformation) {
      setTitle(selectedTransformation.title || '');
      setDescription(selectedTransformation.description || '');
      setFileName(selectedTransformation.fileName || '');
    } else {
      setTitle('');
      setDescription('');
      setFileName('');
    }
  }, [selectedTransformation]);

  const handleSave = async () => {
    if (!selectedTransformation || !workspaceId) return;

    const transformationRef = doc(
      DB,
      `workspaceReviews/${workspaceId}/transformations`,
      selectedTransformation.id
    );
    try {
      await updateDoc(transformationRef, {
        title,
        description,
        fileName,
      });
      setSelectedTransformation(null); // Close sheet on save
    } catch (error) {
      console.error('Error updating document: ', error);
    }
  };

  const handleDeleteSelected = async () => {
    if (!workspaceId || selectedRows.length === 0) return;

    const rowsToDelete = [...selectedRows];
    setSelectedRows([]); // Optimistically clear selection

    try {
      const batch = writeBatch(DB);
      rowsToDelete.forEach((id) => {
        const docRef = doc(DB, `workspaceReviews/${workspaceId}/transformations`, id);
        batch.delete(docRef);
      });

      await batch.commit();
    } catch (error) {
      console.error('Error deleting transformations: ', error);
      setSelectedRows(rowsToDelete); // Revert optimistic update on error
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!user || !workspaceId) {
        console.error('User or workspace not available.');
        return;
      }

      setIsUploading(true);
      try {
        await Promise.all(
          acceptedFiles.map(async (file) => {
            const transformationsRef = doc(
              collection(DB, `workspaceReviews/${workspaceId}/transformations`)
            );
            const path = `transformations/${workspaceId}/${transformationsRef.id}`;
            const { url, mimeType, error } = await uploadTransformation(file, path, {});

            if (error) {
              throw error;
            }

            const fileExtension = getExtensionFromMimeType(mimeType, file.name);

            await setDoc(transformationsRef, {
              imageUrl: url,
              mimeType,
              fileId: transformationsRef.id,
              fileExtension,
              fullFilePath: `https://storage.googleapis.com/simply-public-assets/${path}`,
              fileName: file.name,
              createdAt: serverTimestamp(),
              title: '',
              description: '',
            });
          })
        );
      } catch (error) {
        console.error('Failed to upload files:', error);
      } finally {
        setIsUploading(false);
      }
    },
    [user, workspaceId]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    },
  });

  return (
    <div className="space-y-8 p-4 md:p-8">
      <Alert variant="default" style={{ backgroundColor: REVIEW.lighter }}>
        {/* <Terminal /> */}
        <AlertTitle>
          <b>{t('alert.feature.transformations.new.headline')} 🎉</b>
        </AlertTitle>
        <AlertDescription>
          <Trans
            i18nKey={`alert.feature.transformations.new.description`}
            components={[<strong />]}
          />
        </AlertDescription>
      </Alert>
      <Card
        {...getRootProps()}
        className={`border-2 border-dashed bg-muted/40 transition-colors ${
          isDragActive ? 'border-primary bg-muted' : 'hover:border-primary/60 hover:bg-muted/80'
        }`}
      >
        <input {...getInputProps()} />
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center space-y-2 text-center">
            <div
              className={`flex h-16 w-16 items-center justify-center rounded-lg border ${
                isUploading ? 'animate-spin' : ''
              }`}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="flex items-center space-x-1 pt-2">
              <h3 className="text-lg font-medium">
                {isUploading
                  ? t('transformations.uploading')
                  : isDragActive
                  ? t('transformations.drop_files_here')
                  : t('transformations.upload_progress_pictures')}
              </h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button">
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('transformations.upload_tooltip')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('transformations.drop_files_here_or_browse')}
            </p>
          </div>
        </CardContent>
      </Card>

      {selectedRows.length > 0 && (
        <div className="flex items-center justify-between rounded-md bg-muted p-3">
          <div className="text-sm text-muted-foreground">
            {selectedRows.length} {t('transformations.of')} {transformations.length}{' '}
            {t('transformations.rows_selected')}.
          </div>
          <Button variant="destructive" onClick={handleDeleteSelected}>
            <Trash2 className="mr-2 h-4 w-4" />
            {t('transformations.delete_selected')}
          </Button>
        </div>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={
                    selectedRows.length === transformations.length && transformations.length > 0
                  }
                  onCheckedChange={(checked) => {
                    setSelectedRows(checked ? transformations.map((t) => t.id) : []);
                  }}
                  aria-label={t('transformations.select_all') as string}
                />
              </TableHead>
              <TableHead className="w-[250px]">{t('transformations.name')}</TableHead>
              <TableHead>{t('transformations.title')}</TableHead>
              <TableHead>{t('transformations.description')}</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  {t('transformations.loading_transformations')}
                </TableCell>
              </TableRow>
            ) : (
              transformations.map((item) => (
                <TableRow key={item.id} data-state={selectedRows.includes(item.id) && 'selected'}>
                  <TableCell>
                    <Checkbox
                      checked={selectedRows.includes(item.id)}
                      onCheckedChange={(checked) => {
                        setSelectedRows(
                          checked
                            ? [...selectedRows, item.id]
                            : selectedRows.filter((id) => id !== item.id)
                        );
                      }}
                      aria-label={t('transformations.select_row') as string}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-10 w-10  rounded-sm bg-slate-100">
                        <AvatarImage
                          className="object-cover"
                          src={item.imageUrl}
                          alt={item.fileName}
                        />
                        <AvatarFallback>{item.fileName.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{item.fileName}</span>
                    </div>
                  </TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell className="text-muted-foreground">{item.description}</TableCell>
                  <TableCell>
                    <Sheet
                      open={selectedTransformation?.id === item.id}
                      onOpenChange={(isOpen) => !isOpen && setSelectedTransformation(null)}
                    >
                      <SheetTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setSelectedTransformation(item)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </SheetTrigger>
                      <SheetContent>
                        <SheetHeader>
                          <SheetTitle>{t('transformations.edit_title')}</SheetTitle>
                          <SheetDescription>
                            {t('transformations.edit_description')}
                          </SheetDescription>
                        </SheetHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="fileName" className="text-right">
                              {t('transformations.filename_label')}
                            </Label>
                            <Input
                              id="fileName"
                              value={fileName}
                              onChange={(e) => setFileName(e.target.value)}
                              className="col-span-3"
                            />
                          </div>
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="title" className="text-right">
                              {t('transformations.title_label')}
                            </Label>
                            <Input
                              id="title"
                              value={title}
                              onChange={(e) => setTitle(e.target.value)}
                              className="col-span-3"
                            />
                          </div>
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="description" className="text-right">
                              {t('transformations.description_label')}
                            </Label>
                            <Textarea
                              id="description"
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              className="col-span-3"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-end">
                          <Button onClick={handleSave}>{t('general.save')}</Button>
                        </div>
                        <div className="flex flex-col items-center justify-center p-8">
                          <div className="flex items-center justify-center text-sm mb-2">
                            <Badge variant="outline">{t('widgets.setup.preview.headline')}</Badge>
                          </div>
                          <TransformationImageCard
                            imageUrl={item.imageUrl}
                            title={title}
                            description={description}
                          />
                        </div>
                      </SheetContent>
                    </Sheet>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
